import { useEffect, useRef, useState } from "react";
import type { FramesClient } from "@connect/data/frames/client";
import { t, type TranslationKey } from "@connect/i18n";
import { log } from "@connect/observability";
import { fetchLimitUpgradeStatus, submitLimitUpgrade } from "./limit-upgrade-api";
import type { LimitUpgradeStatus } from "./limit-upgrade-api.types";
import type {
  LimitUpgradePhase,
  UseLimitUpgradeOptions,
  UseLimitUpgradeResult,
} from "./use-limit-upgrade.types";

const MAX_POLL_ATTEMPTS = 30;
const POLL_INTERVAL_MS = 1000;
const SUCCESS_HOLD_MS = 900;

const GENERIC_ERROR: TranslationKey = "challenge.limitUpgrade.errors.generic";
const NOT_ELIGIBLE_ERROR: TranslationKey = "challenge.limitUpgrade.errors.notEligible";
const REJECTED_ERROR: TranslationKey = "challenge.limitUpgrade.errors.rejected";
const VERIFICATION_INCOMPLETE_ERROR: TranslationKey =
  "challenge.limitUpgrade.errors.verificationIncomplete";
const TIMEOUT_ERROR: TranslationKey = "challenge.limitUpgrade.errors.timeout";

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

async function pollUntilResolved(
  client: FramesClient,
  intervalMs: number,
  maxAttempts: number,
): Promise<LimitUpgradeStatus | null> {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    await delay(intervalMs);
    const status = await fetchLimitUpgradeStatus(client).catch((error) => {
      log.warn("Guest checkout limit upgrade status poll failed", { error: errorMessage(error) });
      return null;
    });
    if (status === null) {
      return null;
    }
    if (status !== "pending") {
      return status;
    }
  }
  return "pending";
}

export function useLimitUpgrade({
  client,
  amount,
  asset,
  onComplete,
  onNotEligible,
  onVerificationIncomplete,
  timing,
}: UseLimitUpgradeOptions): UseLimitUpgradeResult {
  const pollIntervalMs = timing?.pollIntervalMs ?? POLL_INTERVAL_MS;
  const maxPollAttempts = timing?.maxPollAttempts ?? MAX_POLL_ATTEMPTS;
  const successHoldMs = timing?.successHoldMs ?? SUCCESS_HOLD_MS;
  const [phase, setPhase] = useState<LimitUpgradePhase>("idle");
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const inFlightRef = useRef(false);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (successTimerRef.current !== null) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  const fail = (message: TranslationKey) => {
    inFlightRef.current = false;
    if (!mountedRef.current) {
      return;
    }
    setError(t(message));
    setPhase("idle");
  };

  const submit = async (dateOfBirth: string, socialSecurityNumberLast4: string) => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setError(null);
    setPhase("submitting");

    const submitted = await submitLimitUpgrade(client, {
      amount,
      asset,
      dateOfBirth,
      socialSecurityNumberLast4,
    }).catch((error) => {
      log.warn("Guest checkout limit upgrade submit failed", { error: errorMessage(error) });
      return null;
    });

    if (!mountedRef.current) {
      return;
    }
    if (submitted === null) {
      fail(GENERIC_ERROR);
      return;
    }
    if (submitted === "notEligible") {
      onNotEligible();
      fail(NOT_ELIGIBLE_ERROR);
      return;
    }
    if (submitted === "verificationIncomplete") {
      onVerificationIncomplete();
      fail(VERIFICATION_INCOMPLETE_ERROR);
      return;
    }

    let resolved: LimitUpgradeStatus | null = submitted;
    if (submitted === "pending") {
      setPhase("polling");
      resolved = await pollUntilResolved(client, pollIntervalMs, maxPollAttempts);
      if (!mountedRef.current) {
        return;
      }
    }

    if (resolved === null) {
      fail(GENERIC_ERROR);
      return;
    }
    if (resolved === "pending") {
      fail(TIMEOUT_ERROR);
      return;
    }
    if (resolved === "rejected") {
      // A durable, persisted review outcome (not a retryable failure) - the
      // partner needs to know it's terminal, same as a successful upgrade.
      onComplete(resolved);
      fail(REJECTED_ERROR);
      return;
    }
    if (resolved !== "upgraded") {
      fail(NOT_ELIGIBLE_ERROR);
      return;
    }

    setPhase("succeeded");
    successTimerRef.current = setTimeout(() => onComplete(resolved), successHoldMs);
  };

  const clearError = () => {
    setError(null);
  };

  return { phase, error, submit, clearError };
}
